import { useEffect, useRef } from 'react';

export const INTERSECTION_STATE_ATTRIBUTE = 'data-intersection-state';

type IntersectionState = 'hidden' | 'visible';

type UseIntersectionObserverOptions = {
  delayMs?: number;
  once?: boolean;
  selector?: string;
  staggerMs?: number;
  threshold?: number;
  triggerRatio?: number;
};

let lastPopStateAt = 0;
let popStateSubscriberCount = 0;

function markHistoryTraversal() {
  lastPopStateAt = Date.now();
}

function subscribeToHistoryTraversal() {
  if (popStateSubscriberCount === 0) {
    window.addEventListener('popstate', markHistoryTraversal);
  }
  popStateSubscriberCount += 1;

  return () => {
    popStateSubscriberCount -= 1;
    if (popStateSubscriberCount === 0) {
      window.removeEventListener('popstate', markHistoryTraversal);
    }
  };
}

function isHistoryTraversal() {
  if (Date.now() - lastPopStateAt < 1500) {
    return true;
  }

  const navigation = performance.getEntriesByType('navigation')[0] as
    | PerformanceNavigationTiming
    | undefined;

  return navigation?.type === 'back_forward';
}

function shouldSkipInitialReveal() {
  return isHistoryTraversal() || window.scrollY > 1 || location.hash !== '';
}

function setIntersectionState(target: HTMLElement, state: IntersectionState) {
  target.dataset.intersectionState = state;
}

export default function useIntersectionObserver<T extends HTMLElement>({
  delayMs = 0,
  once = true,
  selector,
  staggerMs = 0,
  threshold = 0.05,
  triggerRatio = 0.9,
}: UseIntersectionObserverOptions = {}) {
  const containerRef = useRef<T>(null);

  useEffect(() => {
    const unsubscribeFromHistoryTraversal = subscribeToHistoryTraversal();
    let observer: IntersectionObserver | undefined;
    let firstFrame = 0;
    let secondFrame = 0;

    firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(() => {
        const container = containerRef.current;
        if (!container) {
          return;
        }

        const targets = selector
          ? Array.from(container.querySelectorAll<HTMLElement>(selector))
          : [container];
        const reducedMotion = window.matchMedia(
          '(prefers-reduced-motion: reduce)',
        ).matches;
        const skipInitialReveal = shouldSkipInitialReveal();

        targets.forEach((target, index) => {
          target.style.setProperty(
            '--intersection-delay',
            `${delayMs + index * staggerMs}ms`,
          );
        });

        if (
          reducedMotion ||
          skipInitialReveal ||
          !('IntersectionObserver' in window)
        ) {
          targets.forEach((target) => setIntersectionState(target, 'visible'));
          return;
        }

        observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const target = entry.target as HTMLElement;

              if (entry.isIntersecting) {
                setIntersectionState(target, 'visible');
                if (once) {
                  observer?.unobserve(target);
                }
                return;
              }

              const revealBoundary =
                entry.rootBounds?.bottom ?? window.innerHeight * triggerRatio;
              if (!once && entry.boundingClientRect.top >= revealBoundary) {
                setIntersectionState(target, 'hidden');
              }
            });
          },
          {
            rootMargin: `0px 0px -${(1 - triggerRatio) * 100}% 0px`,
            threshold,
          },
        );

        const revealBoundary = window.innerHeight * triggerRatio;
        targets.forEach((target) => {
          const wasRevealed = target.dataset.intersectionState === 'visible';
          const isAtOrAboveViewport =
            target.getBoundingClientRect().top <= revealBoundary;

          if (wasRevealed || isAtOrAboveViewport) {
            setIntersectionState(target, 'visible');
            if (!once) {
              observer?.observe(target);
            }
            return;
          }

          setIntersectionState(target, 'hidden');
          observer?.observe(target);
        });
      });
    });

    return () => {
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      observer?.disconnect();
      unsubscribeFromHistoryTraversal();
    };
  }, [delayMs, once, selector, staggerMs, threshold, triggerRatio]);

  return containerRef;
}
